import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { X, Rocket, ShieldCheck } from 'lucide-react';
import { getReleaseInfo } from '../services/releaseInfo';

/**
 * Standalone "Release Info" modal, opened from the sidebar next to
 * Integrations — same shell shape (gradient header bar, icon + title +
 * subtitle, close button, footer note) but built on this codebase's token
 * classes (bg-surface / text-fg / border-line, as SettingsModal and
 * ThemeSection already use) rather than IntegrationsModal's manual
 * isDarkMode-ternary + hardcoded-hex styling.
 *
 * Fields fall back to the literal string "unknown" (see api/app/config.py)
 * for a local/dev backend or an image built before this stamping existed —
 * shown as-is, same as `discover`'s own admin System page does for the same
 * fields.
 */

function formatBuildDate(value) {
  if (!value || value === 'unknown') return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function shortSha(value) {
  if (!value || value === 'unknown') return value;
  return value.slice(0, 7);
}

const ReleaseInfoModal = ({ isOpen, onClose }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    getReleaseInfo().then((data) => {
      setInfo(data);
      setLoading(false);
    });
  }, [isOpen]);

  if (!isOpen) return null;

  const rows = info
    ? [
        ['Release Date', formatBuildDate(info.build_date)],
        ['Release Version', info.release_version],
        ['Branch', info.git_branch],
        ['Branch SHA', shortSha(info.git_sha)],
      ]
    : [];

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-line bg-surface shadow-2xl">
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4">
          <div
            className="absolute top-0 left-0 right-0 h-1"
            style={{ background: 'linear-gradient(90deg, #06b6d4, #8b5cf6, #ec4899)' }}
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-brand-soft p-2">
                <Rocket className="w-5 h-5 text-brand" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-fg">Release Info</h2>
                <p className="text-xs text-fg-muted">What&apos;s currently deployed</p>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close release info"
              className="rounded-xl p-2 text-fg-muted transition-all hover:bg-surface-raised hover:text-fg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content — same table markup/classes as ServerSideDataTable.jsx
            (chat's own data-table component), for visual consistency with
            every other tabular display in the app. */}
        <div className="px-6 pb-6">
          {loading || !info ? (
            <p className="py-4 text-[13px] text-fg-muted">
              {loading ? 'Loading…' : 'Release info unavailable.'}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-line">
              <table className="min-w-full divide-y divide-line">
                <tbody className="divide-y divide-line bg-surface">
                  {rows.map(([label, value]) => (
                    <tr key={label} className="hover:bg-surface-raised">
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-fg-secondary">
                        {label}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-fg">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-line bg-surface-raised/50 px-6 py-4">
          <div className="flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4 text-fg-muted" />
            <p className="text-xs text-fg-muted">Build metadata only — nothing sensitive</p>
          </div>
        </div>
      </div>
    </div>
  );
};

ReleaseInfoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ReleaseInfoModal;
